import { useState, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Search, UserPlus, ChevronLeft, ChevronRight, Users2, Archive } from 'lucide-react';
import { patientApi } from '@/lib/api';
import { differenceInYears, parseISO } from 'date-fns';
import { useAuthStore } from '@/store/auth.store';

function ageLabel(p: any): string {
  if (p.dob) {
    try { return `${differenceInYears(new Date(), parseISO(p.dob))}y`; } catch {}
  }
  return p.age_years ? `${p.age_years}y` : '—';
}

function genderLabel(g: string | undefined): string {
  if (g === 'male')   return 'M';
  if (g === 'female') return 'F';
  if (g === 'other')  return 'O';
  return '—';
}

export default function PatientDirectory() {
  const navigate        = useNavigate();
  const user            = useAuthStore(s => s.user);
  const isAdmin         = user && ['hospital_admin','owner'].includes(user.role);
  const [input,         setInput]         = useState('');
  const [search,        setSearch]        = useState('');
  const [page,          setPage]          = useState(1);
  const [inclArchived,  setInclArchived]  = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const LIMIT = 20;

  // Debounce input → search, reset page
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setSearch(input.trim());
      setPage(1);
    }, 300);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [input]);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['patients', search, page, LIMIT, inclArchived],
    queryFn:  () => patientApi.list({
      search, page, limit: LIMIT,
      include_archived: inclArchived || undefined,
    }).then(r => r.data),
    placeholderData: (prev) => prev,
  });

  const rows  = data?.data  ?? [];
  const total = data?.total ?? 0;
  const pages = data?.pages ?? 1;

  return (
    <div className="flex flex-col h-full">
      {/* ── Toolbar ─────────────────────────────────────────────── */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-white flex-shrink-0">
        <div className="flex items-center gap-3">
          <Users2 size={20} className="text-navy"/>
          <div>
            <h1 className="text-base font-semibold text-navy">Patient Directory</h1>
            <p className="text-xs text-gray-400">{total.toLocaleString()} patients registered</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 border border-gray-200 rounded-lg px-3 py-1.5 bg-gray-50
                          focus-within:border-navy/40 focus-within:ring-2 focus-within:ring-navy/10 transition">
            <Search size={13} className="text-gray-400 flex-shrink-0"/>
            <input
              value={input}
              onChange={e => setInput(e.target.value)}
              placeholder="Search by name or ID…"
              className="bg-transparent text-sm text-navy outline-none w-52 placeholder:text-gray-400"
            />
          </div>
          {isAdmin && (
            <button
              onClick={() => { setInclArchived(v => !v); setPage(1); }}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-sm font-medium transition ${
                inclArchived
                  ? 'border-amber-400 bg-amber-50 text-amber-700'
                  : 'border-gray-200 bg-white text-gray-500 hover:bg-gray-50'
              }`}>
              <Archive size={13}/>
              {inclArchived ? 'Hiding archived' : 'Include archived'}
            </button>
          )}
          <button
            onClick={() => navigate('/patients/new')}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-orange
                       text-gray-900 text-sm font-semibold hover:bg-orange-dark transition shadow-sm">
            <UserPlus size={14}/>
            New Patient
          </button>
        </div>
      </div>

      {/* ── Table ───────────────────────────────────────────────── */}
      <div className="flex-1 overflow-auto">
        <table className="w-full text-sm border-collapse">
          <thead className="sticky top-0 bg-gray-50 border-b border-gray-200 z-10">
            <tr>
              {['Patient ID', 'Name', 'Age / Gender', 'Mobile', 'ABHA ID', 'City'].map(h => (
                <th key={h} className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr>
                <td colSpan={6} className="text-center py-16 text-gray-400 text-sm">Loading…</td>
              </tr>
            )}
            {isError && (
              <tr>
                <td colSpan={6} className="text-center py-16 text-red-400 text-sm">Failed to load patients.</td>
              </tr>
            )}
            {!isLoading && !isError && rows.length === 0 && (
              <tr>
                <td colSpan={6}>
                  <div className="flex flex-col items-center justify-center py-20 text-gray-400 gap-3">
                    <Users2 size={36} strokeWidth={1.2}/>
                    <p className="text-sm font-medium">
                      {search ? `No patients match "${search}"` : 'No patients registered yet'}
                    </p>
                  </div>
                </td>
              </tr>
            )}
            {rows.map((p: any) => (
              <tr
                key={p.id}
                onClick={() => navigate(`/patients/${p.id}`)}
                className="border-b border-gray-100 hover:bg-orange/5 cursor-pointer transition-colors group">
                <td className="px-5 py-3.5">
                  <span className="font-mono text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded group-hover:bg-orange/10">
                    {p.patient_uid}
                  </span>
                </td>
                <td className="px-5 py-3.5">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-navy max-w-[180px] truncate">{p.name}</span>
                    {p.is_archived && (
                      <span className="flex items-center gap-0.5 text-2xs font-medium px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 flex-shrink-0">
                        <Archive size={9}/> Archived
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-5 py-3.5 whitespace-nowrap">
                  <span className="text-gray-700">{ageLabel(p)}</span>
                  <span className="text-gray-400 mx-1">/</span>
                  <span className="text-gray-700">{genderLabel(p.gender)}</span>
                </td>
                <td className="px-5 py-3.5 font-mono text-xs text-gray-600">
                  {p.mobile ?? <span className="text-gray-300">—</span>}
                </td>
                <td className="px-5 py-3.5 font-mono text-xs text-gray-600 max-w-[140px] truncate">
                  {p.abha_id ?? <span className="text-gray-300">—</span>}
                </td>
                <td className="px-5 py-3.5 text-gray-500 text-xs">
                  {p.city ?? <span className="text-gray-300">—</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* ── Pagination ──────────────────────────────────────────── */}
      {pages > 1 && (
        <div className="flex items-center justify-between px-6 py-3 border-t border-gray-200 bg-white flex-shrink-0">
          <span className="text-xs text-gray-500">
            Showing {((page - 1) * LIMIT) + 1}–{Math.min(page * LIMIT, total)} of {total.toLocaleString()}
          </span>
          <div className="flex items-center gap-1">
            <button
              disabled={page <= 1}
              onClick={() => setPage(p => p - 1)}
              className="p-1.5 rounded hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition">
              <ChevronLeft size={14}/>
            </button>
            <span className="text-xs text-gray-600 px-2">Page {page} of {pages}</span>
            <button
              disabled={page >= pages}
              onClick={() => setPage(p => p + 1)}
              className="p-1.5 rounded hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition">
              <ChevronRight size={14}/>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
